import { useQuery, type QueryClient } from '@tanstack/react-query';
import { getPaymentAvailability } from '../services/portalPaymentService';
import { useDisplaySettings } from './useDisplaySettings';
import { usePortalAuth } from './usePortalAuth';

/** Prefix of the availability query, for invalidating every account's entry. */
export const PAYMENT_AVAILABILITY_QUERY_KEY = [
	'portal',
	'payment-availability',
] as const;

/**
 * PD-16789 — whether to show a Make a Payment button.
 *
 * The server decides (GET /payments/availability): invoice payments on,
 * pricing visible, a gateway and a card or bank type offered, and a payable
 * invoice on any of the user's accounts or Account Funds deposits allowed.
 * `canPayInvoices` from /auth/me covers the first three, so it is checked
 * first and a storefront without payments never asks.
 *
 * Keyed by the active account because the switches the server checks follow
 * it. Hidden while the answer is loading or failed: a missing button is
 * better than one that leads to a screen with nothing to pay.
 */
export function useCanMakePayment(): boolean {
	const { token, isAuthenticated, activeClientId } = usePortalAuth();
	const display = useDisplaySettings();

	const enabled =
		isAuthenticated &&
		token != null &&
		activeClientId != null &&
		display.canPayInvoices;

	const { data } = useQuery({
		queryKey: [...PAYMENT_AVAILABILITY_QUERY_KEY, activeClientId],
		queryFn: () => getPaymentAvailability(token ?? ''),
		enabled,
		staleTime: 60_000,
	});

	return enabled && data?.data.canMakePayment === true;
}

/**
 * PD-16789 — re-ask after a payment: paying the last open invoice can take
 * the button away.
 */
export async function invalidatePaymentAvailability(
	queryClient: QueryClient,
): Promise<void> {
	await queryClient.invalidateQueries({
		queryKey: PAYMENT_AVAILABILITY_QUERY_KEY,
	});
}
